import {useCallback, useState} from 'react';
import {
  QuestionMatchingEvaluationResponse,
  QuestionMatchingEvaluationResponses,
} from '../AiSupport.types';
import {useApi} from '~/modules/api';

function getSavedResponse(id: string) {
  const data = localStorage.getItem(`questionMatchingEvaluation.${id}`);
  if (data) {
    return JSON.parse(data) as QuestionMatchingEvaluationResponses;
  }
  return {};
}

function saveResponse(
  id: string,
  responses: QuestionMatchingEvaluationResponses,
) {
  localStorage.setItem(
    `questionMatchingEvaluation.${id}`,
    JSON.stringify(responses),
  );
}

function clearSavedResponse(id: string) {
  localStorage.removeItem(`questionMatchingEvaluation.${id}`);
}

export const useQuestionMatchingEvaluation = ({
  questionSampleSetId,
}: {
  questionSampleSetId: string;
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const api = useApi();
  const [responses, setResponses] =
    useState<QuestionMatchingEvaluationResponses>(
      getSavedResponse(questionSampleSetId),
    );

  const onSaveResponse = useCallback(
    (response: QuestionMatchingEvaluationResponse) => {
      const newData = {
        ...responses,
        [response.question_id]: response,
      };
      setResponses(newData);
      saveResponse(questionSampleSetId, newData);
    },
    [responses, questionSampleSetId],
  );

  const onSubmitResponses = useCallback(
    ({onSuccess}: {onSuccess: () => void}) => {
      setError(null);
      setSubmitting(true);
      api
        .fetch({
          url: `ai-support/question-samples/${questionSampleSetId}/matching-evaluation`,
          method: 'POST',
          data: {
            response: responses,
          },
        })
        .then(data => {
          setSubmitting(false);
          setResponses({});
          clearSavedResponse(questionSampleSetId);
          onSuccess();
        })
        .catch(error => {
          setError(error);
          setSubmitting(false);
        });
    },
    [api, responses, questionSampleSetId],
  );

  return {
    responses,
    submitting,
    error,
    onSaveResponse,
    onSubmitResponses,
  };
};
